import React, { useState, useEffect, useMemo } from 'react';
import AdminSidebar from '../components/Admin/AdminSidebar';
import AdminNavbar from '../components/Admin/AdminNavbar';
import { motion } from 'framer-motion';
import { API_BASE_URL as API_BASE } from '../config/apiConfig';
import {
    Users,
    ShieldCheck,
    ArrowUpRight,
    Zap,
    Globe,
    Loader2,
    History,
    UserCheck
} from 'lucide-react';
import UserBase from '../components/Admin/UserBase';
import FormulaRepository from '../components/Admin/FormulaRepository';
import LoginLogsTable from '../components/Admin/LoginLogsTable';
import SSLStatusCard from '../components/Admin/SSLStatusCard';
import CityAnalysisSection from '../components/Dashboard/CityAnalysisSection';
import { JURISDICTION_GROUPS } from '../utils/jurisdictionData';
import { AnimatePresence } from 'framer-motion';
import SystemLogs from '../components/Admin/SystemLogs';
import ZoningGlossary from '../components/Admin/ZoningGlossary';
import SubscriptionSettings from '../components/Admin/SubscriptionSettings';
import { getPlanDetails } from '../utils/planUtils';
import { formatTimeToPST } from '../utils/dateUtils';

const Admin = () => {
    const [activeView, setActiveView] = useState('admin');
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [selectedCountyForInsights, setSelectedCountyForInsights] = useState(null);

    // Real Data State
    const [jurisdictions, setJurisdictions] = useState([]);
    const [users, setUsers] = useState([]);
    const [logs, setLogs] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Dynamic Data Derivation
    const jurisdictionsByCounty = useMemo(() => {
        const counts = { 'Santa Clara': 0, 'San Mateo': 0, 'Contra Costa': 0, 'Alameda': 0, 'Other': 0 };
        const cityData = analytics?.city_breakdown || analytics?.jurisdictions_load;

        if (analytics && cityData) {
            const entries = Array.isArray(cityData)
                ? cityData.map(item => [item.city || item.name, item.count])
                : Object.entries(cityData);

            entries.forEach(([cityName, count]) => {
                const group = JURISDICTION_GROUPS.find(g =>
                    (cityName && g.county.toLowerCase() === cityName.toLowerCase()) ||
                    g.cities.some(c =>
                        cityName && (
                            c.toLowerCase() === cityName.toLowerCase() ||
                            c.toUpperCase().replace(/\s+/g, '_') === cityName.toUpperCase() ||
                            (cityName.toUpperCase() === 'LAH' && c === 'Los Altos Hills')
                        )
                    )
                );
                const county = group ? group.county : 'Other';
                counts[county] = (counts[county] || 0) + count;
            });
        } else if (analytics && analytics.county_load) {
            analytics.county_load.forEach(c => {
                const group = JURISDICTION_GROUPS.find(g => g.county.toLowerCase() === c.name.toLowerCase());
                const name = group ? group.county : 'Other';
                counts[name] = (counts[name] || 0) + c.count;
            });
        } else {
            // Fallback to static jurisdiction config counts if no analytics data exists
            jurisdictions.forEach(j => {
                const group = JURISDICTION_GROUPS.find(g =>
                    g.cities.some(c => c.toLowerCase() === j.name.toLowerCase())
                );
                const county = group ? group.county : 'Other';
                counts[county] = (counts[county] || 0) + 1;
            });
        }

        const baseGroups = [...JURISDICTION_GROUPS, { county: 'Other', color: 'bg-slate-400' }];
        return baseGroups.map(g => {
            const color = g.color.replace('bg-', '').replace('-400', '');
            return {
                name: g.county,
                count: counts[g.county] || 0,
                color: color
            };
        }).filter(c => c.count > 0 || c.name !== 'Other');
    }, [jurisdictions, analytics]);

    const recentAudits = useMemo(() => logs.slice(0, 3), [logs]);
    const recentUsers = useMemo(() => users.slice(0, 3), [users]);

    const cityCountsForDrilldown = useMemo(() => {
        if (!selectedCountyForInsights || !analytics?.city_breakdown) return {};
        const counts = {};

        if (selectedCountyForInsights === 'Other') {
            analytics.city_breakdown.forEach(item => {
                const cityName = item.city;
                const isInGroup = JURISDICTION_GROUPS.some(g =>
                    g.cities.some(c =>
                        c.toLowerCase() === cityName.toLowerCase() ||
                        c.toUpperCase().replace(/\s+/g, '_') === cityName.toUpperCase() ||
                        (cityName.toUpperCase() === 'LAH' && c === 'Los Altos Hills')
                    )
                );
                if (!isInGroup) {
                    counts[cityName] = (counts[cityName] || 0) + item.count;
                }
            });
        } else {
            const countyData = JURISDICTION_GROUPS.find(g => g.county === selectedCountyForInsights);
            if (!countyData) return {};

            analytics.city_breakdown.forEach(item => {
                const cityName = item.city;
                // Map the backend city name to the frontend city list
                const matchedCity = countyData.cities.find(c =>
                    c.toLowerCase() === cityName.toLowerCase() ||
                    c.toUpperCase().replace(/\s+/g, '_') === cityName.toUpperCase() ||
                    (cityName.toUpperCase() === 'LAH' && c === 'Los Altos Hills')
                );
                if (matchedCity) {
                    counts[matchedCity] = (counts[matchedCity] || 0) + item.count;
                }
            });
        }
        return counts;
    }, [selectedCountyForInsights, analytics]);

    useEffect(() => {
        const fetchAdminData = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('accessToken');
                const headers = { 'Authorization': `Bearer ` + token };

                const [jRes, uRes, lRes, aRes] = await Promise.all([
                    fetch(`${API_BASE}/admin/formulae`, { headers }),
                    fetch(`${API_BASE}/admin/users`, { headers }),
                    fetch(`${API_BASE}/admin/login-logs`, { headers }),
                    fetch(`${API_BASE}/admin/analytics/summary`, { headers })
                ]);

                if (jRes.ok) {
                    const data = await jRes.json();
                    setJurisdictions(data);
                }
                if (uRes.ok) setUsers(await uRes.json());
                if (lRes.ok) setLogs(await lRes.json());
                if (aRes.ok) setAnalytics(await aRes.json());
            } catch (err) {
                console.error("Critical failure in admin telemetry:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAdminData();
    }, []);

    const renderContent = () => {
        switch (activeView) {
            case 'admin':
                if (isLoading) {
                    return (
                        <div className="flex flex-col items-center justify-center min-h-[60vh]">
                            <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mb-4" />
                            <p className="text-slate-500 font-medium">Synchronizing Secure Nodes...</p>
                        </div>
                    );
                }

                const maxCount = Math.max(...jurisdictionsByCounty.map(c => c.count), 10);
                const maxChartValue = Math.ceil(maxCount / 20) * 20;

                return (
                    <div className="space-y-4 animate-in fade-in duration-500">
                        {/* Professional Header Section */}
                        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
                            <div className="flex flex-col gap-2 pl-4">
                                <div className="flex items-center space-x-3 mb-1">
                                    <div className="w-10 h-1 bg-indigo-600 rounded-full"></div>
                                    <h1 className="text-5xl font-black text-slate-900 tracking-tighter leading-none flex items-center gap-4">
                                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-500">Command <span className="text-indigo-600">Center</span></span>
                                        <div className="h-2 w-2 rounded-full bg-indigo-500 mt-2 animate-pulse" />
                                    </h1>
                                </div>
                                <p className="text-slate-500 font-bold text-sm mt-1 max-w-xl leading-relaxed">
                                    Administrative Control Protocol / <span className="text-indigo-600 font-black">{jurisdictions.length} Nodes Monitoring</span>
                                </p>
                            </div>
                        </div>

                        {/* Pure SaaS Professional Stats Matrix */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[
                                { label: 'Supported Jurisdictions', value: jurisdictions.length, icon: Globe, color: 'indigo', status: 'Verified' },
                                { label: 'Total Members', value: users.length, icon: Users, color: 'emerald', status: 'Active' },
                                { label: 'Active Subscriptions', value: users.filter(u => u.subscription_status === 'active').length, icon: Zap, color: 'purple', status: 'Live' },
                                { label: 'Total Logins', value: logs.length, icon: ShieldCheck, color: 'orange', status: 'Stored' },
                            ].map((stat, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    className="bg-white p-7 rounded-3xl border border-slate-200 shadow-sm hover:shadow-xl hover:shadow-slate-100 transition-all group overflow-hidden relative"
                                >
                                    <div className="flex items-center justify-between relative z-10">
                                        <div className={`w-12 h-12 rounded-2xl bg-${stat.color}-50 flex items-center justify-center border border-${stat.color}-100 group-hover:bg-${stat.color}-500 transition-colors duration-500`}>
                                            <stat.icon className={`w-5 h-5 text-${stat.color}-600 group-hover:text-white transition-colors`} />
                                        </div>
                                        <div className="flex items-center space-x-1.5 px-2.5 py-1 bg-slate-50 border border-slate-100 rounded-lg">
                                            <div className={`w-1.5 h-1.5 rounded-full bg-${stat.color}-500 animate-pulse`} />
                                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{stat.status}</span>
                                        </div>
                                    </div>

                                    <div className="mt-4 relative z-10">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5">{stat.label}</p>
                                        <h3 className="text-4xl font-black text-slate-900 tracking-tighter tabular-nums leading-none">
                                            {stat.value}
                                        </h3>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Jurisdictional Density Matrix */}
                            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-[500px]">
                                <AnimatePresence mode="wait">
                                    {!selectedCountyForInsights ? (
                                        <motion.div
                                            key="matrix"
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 20 }}
                                            className="flex-1 flex flex-col"
                                        >
                                            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between bg-white">
                                                <div>
                                                    <h2 className="text-lg font-bold text-slate-900 leading-none tracking-tight">Analytical Load Matrix</h2>
                                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-1.5 flex items-center">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mr-2"></span>
                                                        Property Search Intensity by Region
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="px-12 pt-16 pb-12 flex-1 flex flex-col justify-center">
                                                <div className="relative h-52 w-full">
                                                    <div className="absolute inset-x-0 inset-y-0 flex flex-col justify-between pointer-events-none px-4">
                                                        {[...Array(6)].map((_, i) => (
                                                            <div key={i} className="w-full border-t border-slate-100 relative">
                                                                <span className="absolute -left-10 -top-2 text-[10px] font-bold text-slate-300 tabular-nums">
                                                                    {Math.round((5 - i) * (maxChartValue / 5))}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="relative h-full w-full flex items-end justify-between px-12">
                                                        {jurisdictionsByCounty.map((c, i) => (
                                                            <div key={i} className="relative flex flex-col items-center w-full max-w-[60px]">
                                                                <div className="relative w-14 h-52 flex items-end justify-center">
                                                                    <motion.div
                                                                        initial={{ height: 0 }}
                                                                        animate={{ height: `${(c.count / maxChartValue) * 100}%` }}
                                                                        whileHover={{ scaleX: 1.1, translateY: -5 }}
                                                                        onClick={() => setSelectedCountyForInsights(c.name)}
                                                                        transition={{ duration: 1, delay: i * 0.1, ease: "circOut" }}
                                                                        className={`w-full bg-gradient-to-t rounded-t-xl shadow-lg relative group cursor-pointer ${c.color === 'blue' ? 'from-blue-600 to-blue-400 shadow-blue-500/20' :
                                                                            c.color === 'rose' ? 'from-rose-600 to-rose-400 shadow-rose-500/20' :
                                                                                c.color === 'indigo' ? 'from-indigo-600 to-indigo-400 shadow-indigo-500/20' :
                                                                                    c.color === 'amber' ? 'from-amber-600 to-amber-400 shadow-amber-500/20' :
                                                                                        'from-slate-600 to-slate-400 shadow-slate-500/20'
                                                                            }`}
                                                                    >
                                                                        <div className="absolute inset-x-0 top-0 h-4 bg-white/20 rounded-t-xl" />
                                                                        <motion.div
                                                                            initial={{ opacity: 0, y: 10 }}
                                                                            animate={{ opacity: 1, y: 0 }}
                                                                            transition={{ delay: 0.8 + (i * 0.1) }}
                                                                            className="absolute -top-12 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-2 py-1 rounded-md text-[10px] font-black shadow-xl"
                                                                        >
                                                                            <span className="tabular-nums">{c.count}</span>
                                                                            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45" />
                                                                        </motion.div>
                                                                    </motion.div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="flex justify-between px-[68px] mt-10">
                                                    {jurisdictionsByCounty.map((c, i) => (
                                                        <div key={i} className="flex flex-col items-center w-[60px]">
                                                            <span className="text-[11px] font-black text-slate-800 uppercase tracking-widest">{c.name.split(' ')[0]}</span>
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-0.5">{c.name.split(' ')[1] || 'Region'}</span>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* System Telemetry Bar */}
                                                <div className="pt-8 border-t border-slate-100 mt-12 flex items-center justify-between">
                                                    <div className="flex items-center space-x-14">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Infrastructure Capacity</span>
                                                            <div className="flex items-baseline space-x-2">
                                                                <span className="text-4xl font-black text-slate-900 leading-none">{analytics?.total_analyses || 0}</span>
                                                                <span className="text-[11px] font-bold text-indigo-600 uppercase">Properties Analyzed</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button onClick={() => setActiveView('formulas')} className="flex items-center space-x-3 px-10 py-4 bg-slate-900 text-white rounded-2xl font-black text-[11px] uppercase tracking-widest hover:bg-slate-800 shadow-2xl shadow-slate-200 transition-all hover:-translate-y-1 group">
                                                        <span>Full Registry</span>
                                                        <ArrowUpRight className="w-5 h-5 text-indigo-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ) : (
                                        <CityAnalysisSection
                                            selectedCounty={selectedCountyForInsights}
                                            externalCityCounts={cityCountsForDrilldown}
                                            onBack={() => setSelectedCountyForInsights(null)}
                                        />
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Right Sidebar: Dual Analytics */}
                            <div className="space-y-5">
                                {/* Recent Active Sessions */}
                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                                    <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-white">
                                        <h2 className="text-[13px] font-black text-slate-800 uppercase tracking-widest flex items-center">
                                            <span className="w-1 h-1 rounded-full bg-emerald-500 mr-2 animate-pulse"></span>
                                            Recent Active Users
                                        </h2>
                                        <button
                                            onClick={() => setActiveView('loginlogs')}
                                            className="flex items-center space-x-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all group"
                                        >
                                            <span>Archive</span>
                                            <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                        </button>
                                    </div>
                                    <div className="p-1">
                                        {recentAudits.map((log, i) => (
                                            <div key={i} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors rounded-xl group">
                                                <div className="flex items-center space-x-4 min-w-0">
                                                    <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center border border-slate-100 group-hover:bg-white transition-colors flex-shrink-0">
                                                        <Users className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-colors" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-[13px] font-black text-slate-900 truncate">{log.full_name || log.email?.split('@')[0] || 'Unknown User'}</p>
                                                        <p className="text-[11px] font-bold text-slate-400 tabular-nums">Session Verified</p>
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <p className="text-[10px] font-black text-slate-900 tabular-nums">{formatTimeToPST(log.login_time)}</p>
                                                    <p className={`text-[8px] font-black uppercase tracking-tighter ${getPlanDetails(log.plan_name, log.subscription_status).colors.split(' ')[1]}`}>
                                                        {getPlanDetails(log.plan_name, log.subscription_status).label}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                        {recentAudits.length === 0 && (
                                            <div className="p-8 text-center">
                                                <History className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Awaiting Active Nodes</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Recent User Registry */}
                                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                                    <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-white">
                                        <h2 className="text-[13px] font-black text-slate-800 uppercase tracking-widest flex items-center">
                                            <UserCheck className="w-3.5 h-3.5 mr-2 text-indigo-500" />
                                            User Registry
                                        </h2>
                                        <button
                                            onClick={() => setActiveView('users')}
                                            className="flex items-center space-x-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all group"
                                        >
                                            <span>Directory</span>
                                            <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                        </button>
                                    </div>
                                    <div className="p-1">
                                        {recentUsers.map((user, i) => (
                                            <div key={i} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors rounded-xl group">
                                                <div className="flex items-center space-x-4 min-w-0">
                                                    <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-white text-[10px] font-black uppercase ring-4 ring-slate-50 group-hover:ring-indigo-50 transition-all flex-shrink-0">
                                                        {user.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-[13px] font-black text-slate-900 uppercase tracking-tight truncate">{user.full_name || user.email?.split('@')[0]}</p>
                                                        <p className="text-[11px] font-bold text-slate-400 truncate">{user.email}</p>
                                                        <p className={`text-[7px] font-black uppercase tracking-[0.1em] mt-1 ${getPlanDetails(user.plan_name, user.subscription_status).colors.split(' ')[1]}`}>
                                                            {getPlanDetails(user.plan_name, user.subscription_status).label}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="px-2 py-1 bg-indigo-50 rounded text-[9px] font-black text-indigo-600 uppercase flex-shrink-0 ml-2">
                                                    {user.role || 'Member'}
                                                </div>
                                            </div>
                                        ))}
                                        {recentUsers.length === 0 && (
                                            <div className="p-8 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                No users registered
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'users':
                return <UserBase />;
            case 'loginlogs':
                return <LoginLogsTable />;
            case 'formulas':
                return <FormulaRepository />;
            case 'zoningdetails':
                return <ZoningGlossary />;
            case 'subscriptionsettings':
                return <SubscriptionSettings />;
            case 'ssl':
                return (
                    <div className="px-4 py-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
                        <div className="mb-8">
                            <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                                Security <span className="text-indigo-600">Protocol</span>
                                <div className="h-2 w-2 rounded-full bg-indigo-500 animate-pulse" />
                            </h1>
                            <p className="text-slate-500 font-bold text-xs mt-1 uppercase tracking-widest">System Certification & Health Monitoring</p>
                        </div>
                        <SSLStatusCard />
                    </div>
                );
            case 'systemlogs':
                return <SystemLogs />;
            default:
                return (
                    <div className="flex flex-col items-center justify-center min-h-[70vh]">
                        <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mb-4" />
                        <p className="text-slate-500 font-medium">Initializing Management Layer...</p>
                    </div>
                );
        }
    };

    return (
        <div className="flex h-screen bg-slate-50">
            <AdminSidebar
                activeView={activeView}
                setActiveView={setActiveView}
                isMobileOpen={isMobileOpen}
                setIsMobileOpen={setIsMobileOpen}
            />

            <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
                <AdminNavbar
                    activeView={activeView}
                    setIsMobileOpen={setIsMobileOpen}
                />

                <main className="flex-1 p-6 overflow-y-auto no-scrollbar">
                    <div>
                        {renderContent()}
                    </div>
                </main>
            </div>

            <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;500;600;700;800&display=swap');
            
            body {
              font-family: 'Public Sans', sans-serif;
              -webkit-font-smoothing: antialiased;
            }

            .no-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .no-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }

            ::selection {
              background: #4F46E5;
              color: white;
            }
          `}</style>
        </div>
    );
};

export default Admin;
